/**
 * AI CONTEXT
 * Purpose: one write at a time per Standout Review control. `pending` drives
 *   the antd `loading` prop; the ref turns away a second click that lands
 *   before React re-renders the control as loading.
 * Used by: StandoutMomentCard, MomentEditPopover, DontShareReasonModal callers,
 *   ClassImageReviewList, StoryTimeReviewList, MomentArtworkStrip,
 *   StandoutReviewStream (Send to Parents).
 */
import { useCallback, useRef, useState } from "react"

/** Resolves `undefined` without running `work` while a call is in flight. */
export const useSingleFlight = () => {
  const inFlight = useRef(false)
  const [pending, setPending] = useState(false)

  const run = useCallback(
    async <T>(work: () => Promise<T>): Promise<T | undefined> => {
      if (inFlight.current) return undefined
      inFlight.current = true
      setPending(true)
      try {
        return await work()
      } finally {
        inFlight.current = false
        setPending(false)
      }
    },
    []
  )

  return { pending, run }
}

/** Same as `useSingleFlight`, but each key (a row, a student group) has its own lock. */
export const useKeyedSingleFlight = () => {
  const inFlight = useRef(new Set<string>())
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  )

  const run = useCallback(
    async <T>(key: string, work: () => Promise<T>): Promise<T | undefined> => {
      if (inFlight.current.has(key)) return undefined
      inFlight.current.add(key)
      setPendingKeys(new Set(inFlight.current))
      try {
        return await work()
      } finally {
        inFlight.current.delete(key)
        setPendingKeys(new Set(inFlight.current))
      }
    },
    []
  )

  return { pendingKeys, run }
}
